import { blueprintsByKey, experimentalEffectsByKey, modulesBySymbol } from "./data.ts";
import { findBlueprintForFdName } from "./engineering.ts";
import { blueprintsForModule, edsyRuleForModule, experimentalEffectsForModule, moduleFitsSlot, type BuildFit } from "./outfitting.ts";
import type { ModuleDef, Ship, ShipBuildSlot } from "./types.ts";
import edsyRules from "../data/edsy-outfitting.json";

export interface FitModule {
  slotName: string;
  slotKind: ShipBuildSlot["kind"];
  slotIndex: number;
  moduleSymbol: string | null;
  moduleName: string | null;
  size: number | null;
  engineering: {
    blueprintFdName: string | null;
    blueprintName: string | null;
    grade: number | null;
    experimentalEffect: string | null;
  } | null;
}

export function journalSlotForShip(ship: Ship, name: string): ShipBuildSlot | undefined {
  const lower = name.toLowerCase();
  return ship.buildSlots.find(slot => slot.fdSlotName?.toLowerCase() === lower)
    ?? ship.buildSlots.find(slot => slot.slotName.toLowerCase() === lower);
}

export function journalFitForShip(ship: Ship, entries: Array<{
  slot: string; symbol: string; name: string | null; displayName?: string; size: number | null;
  engineered: boolean; blueprintFdName: string | null; blueprintName: string | null;
  grade: number | null; experimentalFdName?: string | null; experimentalName?: string | null;
}>): FitModule[] {
  return entries.flatMap(entry => {
    if (/decal|paintjob|shipkit|bobble|voicepack|cockpit|cargohatch/i.test(entry.slot)) return [];
    const slot = journalSlotForShip(ship, entry.slot);
    if (!slot) return [{ slotName: entry.slot, slotKind: "optional", slotIndex: 0, moduleSymbol: entry.symbol, moduleName: entry.displayName ?? entry.name, size: entry.size, engineering: null }];
    return [{ slotName: slot.slotName, slotKind: slot.kind, slotIndex: slot.index, moduleSymbol: entry.symbol, moduleName: entry.displayName ?? entry.name, size: entry.size,
      engineering: entry.engineered ? { blueprintFdName: entry.blueprintFdName, blueprintName: entry.blueprintName, grade: entry.grade, experimentalEffect: entry.experimentalFdName ?? entry.experimentalName ?? null } : null }];
  });
}

export function moduleForFit(slot: ShipBuildSlot, module: ModuleDef): FitModule {
  return { slotName: slot.slotName, slotKind: slot.kind, slotIndex: slot.index,
    moduleSymbol: module.symbol, moduleName: module.name, size: module.class, engineering: null };
}

export function defaultFitForShip(ship: Ship): FitModule[] {
  return ship.defaultModules.flatMap(entry => {
    const slot = ship.buildSlots.find(slot => slot.slotName === entry.slotName);
    const definition = modulesBySymbol.get(entry.moduleSymbol.toLowerCase());
    return slot && definition ? [moduleForFit(slot, definition)] : [];
  });
}

export function fitsForShip(ship: Ship, entries: FitModule[]): BuildFit[] {
  return entries.flatMap(entry => {
    const slot = ship.buildSlots.find(slot => slot.slotName === entry.slotName);
    const module = modulesBySymbol.get(entry.moduleSymbol?.toLowerCase() ?? "");
    if (!slot || !module || !moduleFitsSlot(ship, slot, module).ok) return [];
    const bulkhead = slot.kind === "bulkhead" ? ship.bulkheads.find(item => item.symbol?.toLowerCase() === module.symbol.toLowerCase()) ?? null : null;
    const effect = entry.engineering?.experimentalEffect;
    const experimentalKey = effect ? [...experimentalEffectsByKey.values()].find(item => item.key === effect || item.name === effect)?.key ?? effect : null;
    return [{ slot, module: bulkhead ? null : module, bulkhead,
      ...(entry.engineering ? { engineering: entry.engineering } : {}), experimentalKey }];
  });
}

/** Validate every write as a whole fit; UI filtering alone is not a security or compatibility boundary. */
export function shipFitIssues(ship: Ship, entries: FitModule[]): string[] {
  const issues: string[] = [];
  const seen = new Set<string>();
  const limits = new Map<string, number>();
  for (const entry of entries) {
    if (seen.has(entry.slotName)) issues.push(`Duplicate slot: ${entry.slotName}.`);
    seen.add(entry.slotName);
    if (!entry.moduleSymbol) continue;
    const slot = ship.buildSlots.find(slot => slot.slotName === entry.slotName);
    const module = modulesBySymbol.get(entry.moduleSymbol.toLowerCase());
    if (!slot || slot.kind !== entry.slotKind) { issues.push(`Unknown or incorrectly typed slot: ${entry.slotName}.`); continue; }
    if (!module) { issues.push(`Unverified module: ${entry.moduleName ?? entry.moduleSymbol}.`); continue; }
    const fit = moduleFitsSlot(ship, slot, module);
    if (!fit.ok) issues.push(`${entry.slotName}: ${fit.reason}`);
    const limit = edsyRuleForModule(module)?.limit;
    if (limit) {
      const count = (limits.get(limit) ?? 0) + 1;
      limits.set(limit, count);
      // This supplied snapshot predates weapon stabilisers, so its AX cap cannot validate modern fits.
      const maximum = limit === "hex" ? undefined : (edsyRules.limits as Record<string, number>)[limit];
      if (maximum && count > maximum) issues.push(`${module.name}: exceeds the EDSY limit of ${maximum} for this module type.`);
    }
    const engineering = entry.engineering;
    if (engineering?.blueprintFdName) {
      const blueprint = blueprintsForModule(module).find(item => item.fdName === engineering.blueprintFdName);
      if (!blueprint) issues.push(`${module.name}: incompatible blueprint.`);
      else if (!blueprint.grades.some(item => item.grade === engineering.grade)) issues.push(`${module.name}: unsupported blueprint grade.`);
    }
    const effect = engineering?.experimentalEffect;
    if (effect && !experimentalEffectsForModule(module).some(item => item.key === effect || item.name === effect)) issues.push(`${module.name}: incompatible experimental effect.`);
  }
  return issues;
}

export function fitExportCode(ship: Ship, name: string, modules: FitModule[]): string {
  return JSON.stringify({ format: "edcc-build", version: 1, ship: ship.slug, name,
    modules: modules.map(module => {
      const engineering = module.engineering;
      const effect = engineering?.experimentalEffect;
      const experimental = effect ? [...experimentalEffectsByKey.values()].find(item => item.key === effect || item.name === effect)?.key : null;
      return { slot: module.slotName, kind: module.slotKind, index: module.slotIndex,
        symbol: module.moduleSymbol, name: module.moduleName, size: module.size,
        ...(engineering?.blueprintFdName ? { blueprint: engineering.blueprintFdName, grade: engineering.grade } : {}),
        ...(experimental ? { experimental } : {}) };
    }),
  });
}
